import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  useTranslation,
} from 'react-i18next';

import {
  LICENSE_MODES,
  PRODUCT_CATALOG,
} from '../../product-catalog/productCatalog';

import {
  getLicenseBundle,
} from '../services/licenseService';

import {
  calculateRemainingDays,
  formatLicenseDate,
  maskLicenseKey,
} from '../utils/licenseUtils';

import './LicenseInfoPage.css';

const LICENSE_BORDER_LEVELS =
  Object.freeze({
    NORMAL: 'normal',
    WARNING: 'warning',
    CRITICAL: 'critical',
    NEUTRAL: 'neutral',
  });

const LICENSE_STATUS = Object.freeze({
  ACTIVE: 'active',
  EXPIRING: 'expiring',
  EXPIRED: 'expired',
  NOT_APPLICABLE: 'notApplicable',
});

const LICENSE_WARNING_DAYS = 100;

function getLicenseVisualState(
  remainingDays,
) {
  if (
    remainingDays === null ||
    remainingDays === undefined ||
    !Number.isFinite(
      Number(remainingDays),
    )
  ) {
    return {
      status:
        LICENSE_STATUS.NOT_APPLICABLE,

      borderLevel:
        LICENSE_BORDER_LEVELS.NEUTRAL,
    };
  }

  const normalizedRemainingDays =
    Number(remainingDays);

  if (
    normalizedRemainingDays <= 0
  ) {
    return {
      status:
        LICENSE_STATUS.EXPIRED,

      borderLevel:
        LICENSE_BORDER_LEVELS.CRITICAL,
    };
  }

  if (
    normalizedRemainingDays <=
    LICENSE_WARNING_DAYS
  ) {
    return {
      status:
        LICENSE_STATUS.EXPIRING,

      borderLevel:
        LICENSE_BORDER_LEVELS.WARNING,
    };
  }

  return {
    status:
      LICENSE_STATUS.ACTIVE,

    borderLevel:
      LICENSE_BORDER_LEVELS.NORMAL,
  };
}

function getNearestLicenseItem(
  licenseItems,
) {
  if (
    !Array.isArray(licenseItems) ||
    licenseItems.length === 0
  ) {
    return null;
  }

  return licenseItems.reduce(
    (
      nearestItem,
      currentItem,
    ) => {
      if (!nearestItem) {
        return currentItem;
      }

      const nearestDays =
        Number(
          nearestItem.remainingDays,
        );

      const currentDays =
        Number(
          currentItem.remainingDays,
        );

      if (
        !Number.isFinite(
          currentDays,
        )
      ) {
        return nearestItem;
      }

      if (
        !Number.isFinite(
          nearestDays,
        ) ||
        currentDays < nearestDays
      ) {
        return currentItem;
      }

      return nearestItem;
    },
    null,
  );
}

export default function LicenseInfoPage() {
  const {
    t,
    i18n,
  } = useTranslation('app');

  const [
    visibleLicenseKeys,
    setVisibleLicenseKeys,
  ] = useState({});

  const [
    licenseState,
    setLicenseState,
  ] = useState({
    status: 'loading',
    machineCounts: [],
    siteTerm: null,
    hiLink: null,
    tms: null,
    error: null,
  });

  useEffect(() => {
    let isActive = true;

    const loadLicenseBundle = async () => {
      setLicenseState((previousState) => ({
        ...previousState,
        status: 'loading',
        error: null,
      }));

      try {
        const {
          machineCounts,
          siteTerm,
          hiLink,
          tms,
        } = await getLicenseBundle();

        if (!isActive) {
          return;
        }

        setLicenseState({
          status: 'ready',
          machineCounts:
            Array.isArray(machineCounts)
              ? machineCounts
              : [],
          siteTerm:
            siteTerm || null,
          hiLink: hiLink || null,
          tms: tms || null,
          error: null,
        });
      } catch (error) {
        if (!isActive) {
          return;
        }

        if (
          process.env.NODE_ENV !==
          'production'
        ) {
          console.error(
            '[LicenseInfoPage] license bundle load failed',
            {
              status:
                error?.status,
              code: error?.code,
              message:
                error?.message,
            },
          );
        }

        setLicenseState({
          status: 'error',
          machineCounts: [],
          siteTerm: null,
          hiLink: null,
          tms: null,
          error,
        });
      }
    };

    loadLicenseBundle();

    return () => {
      isActive = false;
    };
  }, []);

  const language =
    i18n.resolvedLanguage ||
    i18n.language ||
    'ko';

  const machineProducts =
    useMemo(
      () => {
        const machineCountMap =
          new Map(
            licenseState.machineCounts.map(
              (item) => [
                String(
                  item.productCode || '',
                ).toUpperCase(),
                item,
              ],
            ),
          );

        return PRODUCT_CATALOG.filter(
          (product) =>
            product.machine?.managed === true,
        ).map((product) => {
          const machineInfo =
            machineCountMap.get(
              product.code,
            ) || null;

          return {
            ...product,
            machineInformation: {
              registeredCount: Number(
                machineInfo
                  ?.registeredMachineCount || 0,
              ),
              maximumCount:
                Number.isFinite(
                  Number(
                    machineInfo
                      ?.maximumMachineCount,
                  ),
                )
                  ? Number(
                    machineInfo
                      ?.maximumMachineCount,
                  )
                  : null,
              hasLimit:
                product.machine
                  ?.hasLimit === true,
            },
          };
        });
      },
      [licenseState.machineCounts],
    );

  const licenseProducts =
    useMemo(
      () =>
        PRODUCT_CATALOG.filter(
          (product) =>
            product.license !== null,
        ).map((product) => {
          let licenseInformation = {
            mode:
              product.license?.mode ||
              LICENSE_MODES.NONE,
            hasLicense: false,
            key: null,
            issuedAt: null,
            expiresAt: null,
            items: [],
          };

          if (product.code === 'HI_LINK') {
            licenseInformation = {
              ...licenseInformation,
              mode: LICENSE_MODES.TERM,
              hasLicense:
                Boolean(
                  licenseState.hiLink
                    ?.hasLicense,
                ),
              key:
                licenseState.hiLink
                  ?.licenseKeyMasked ||
                null,
              issuedAt:
                licenseState.hiLink
                  ?.issuedAt ||
                null,
              expiresAt:
                licenseState.hiLink
                  ?.expiresAt ||
                null,
            };
          }

          if (
            product.code === 'HI_CPS' ||
            product.code ===
              'PG_SOLUTION'
          ) {
            licenseInformation = {
              ...licenseInformation,
              mode: LICENSE_MODES.TERM,
              hasLicense: Boolean(
                licenseState.siteTerm
                  ?.expiresAt,
              ),
              key: null,
              issuedAt:
                licenseState.siteTerm
                  ?.issuedAt ||
                null,
              expiresAt:
                licenseState.siteTerm
                  ?.expiresAt ||
                null,
            };
          }

          if (product.code === 'TMS') {
            const tmsItems =
              Array.isArray(
                licenseState.tms?.items,
              )
                ? licenseState.tms.items.map(
                  (item) => ({
                    id: item.id,
                    machineId:
                      item.machineId,
                    machineName:
                      item.machineName,
                    key:
                      item.licenseKeyMasked,
                    issuedAt:
                      item.issuedAt,
                    expiresAt:
                      item.expiresAt,
                    remainingDays:
                      item.remainingDays,
                  }),
                )
                : [];

            licenseInformation = {
              ...licenseInformation,
              mode:
                LICENSE_MODES.PER_MACHINE,
              items: tmsItems,
            };
          }

            if (
              licenseInformation?.mode ===
              LICENSE_MODES.TERM
            ) {
              const remainingDays =
                licenseInformation.expiresAt
                  ? calculateRemainingDays(
                    licenseInformation.expiresAt,
                  )
                  : null;

              const visualState =
                getLicenseVisualState(
                  remainingDays,
                );

              return {
                ...product,
                licenseInformation,
                remainingDays,
                status:
                  visualState.status,
                borderLevel:
                  visualState.borderLevel,
              };
            }

            if (
              licenseInformation?.mode ===
              LICENSE_MODES.PER_MACHINE
            ) {
              const licenseItems =
                Array.isArray(
                  licenseInformation.items,
                )
                  ? licenseInformation.items.map(
                    (licenseItem) => {
                      const remainingDays =
                        licenseItem.expiresAt
                          ? calculateRemainingDays(
                            licenseItem.expiresAt,
                          )
                          : null;

                      const visualState =
                        getLicenseVisualState(
                          remainingDays,
                        );

                      return {
                        ...licenseItem,
                        remainingDays,
                        status:
                          visualState.status,
                        borderLevel:
                          visualState.borderLevel,
                      };
                    },
                  )
                  : [];

              const nearestLicenseItem =
                getNearestLicenseItem(
                  licenseItems,
                );

              return {
                ...product,

                licenseInformation: {
                  ...licenseInformation,
                  items: licenseItems,
                },

                remainingDays:
                  nearestLicenseItem
                    ?.remainingDays ??
                  null,

                status:
                  nearestLicenseItem
                    ?.status ??
                  LICENSE_STATUS
                    .NOT_APPLICABLE,

                borderLevel:
                  nearestLicenseItem
                    ?.borderLevel ??
                  LICENSE_BORDER_LEVELS
                    .NEUTRAL,
              };
            }

            return {
              ...product,
              licenseInformation,
              remainingDays: null,

              status:
                LICENSE_STATUS
                  .NOT_APPLICABLE,

              borderLevel:
                LICENSE_BORDER_LEVELS
                  .NEUTRAL,
            };
          }),
        [
          licenseState.hiLink,
          licenseState.tms,
          licenseState.siteTerm,
        ],
    );

  const overallStatus =
    useMemo(
      () => {
        const allLicenseItems = [];

        licenseProducts.forEach(
          (product) => {
            const license =
              product.licenseInformation;

            if (
              license?.mode ===
              LICENSE_MODES.TERM &&
              license.hasLicense
            ) {
              allLicenseItems.push({
                productId:
                  product.id,

                remainingDays:
                  product.remainingDays,

                borderLevel:
                  product.borderLevel,
              });
            }

            if (
              license?.mode ===
              LICENSE_MODES.PER_MACHINE
            ) {
              const items =
                Array.isArray(
                  license.items,
                )
                  ? license.items
                  : [];

              items.forEach(
                (licenseItem) => {
                  allLicenseItems.push({
                    productId:
                      product.id,

                    remainingDays:
                      licenseItem
                        .remainingDays,

                    borderLevel:
                      licenseItem
                        .borderLevel,
                  });
                },
              );
            }
          },
        );

        const warningLicenseCount =
          allLicenseItems.filter(
            (licenseItem) =>
              licenseItem.borderLevel ===
              LICENSE_BORDER_LEVELS.WARNING,
          ).length;

        const criticalLicenseCount =
          allLicenseItems.filter(
            (licenseItem) =>
              licenseItem.borderLevel ===
              LICENSE_BORDER_LEVELS.CRITICAL,
          ).length;

        return {
          totalLicenseCount:
            allLicenseItems.length,

          warningLicenseCount,
          criticalLicenseCount,
        };
      },
      [
        licenseProducts,
      ],
    );

  if (licenseState.status === 'loading') {
    return (
      <div className="member-page">
        <section className="license-information-section">
          <header className="license-section-heading">
            <h2>
              {t('member.overview.title')}
            </h2>
            <p>{t('state.loading')}</p>
          </header>
        </section>
      </div>
    );
  }

  const toggleLicenseKey = (
    visibilityId,
  ) => {
    setVisibleLicenseKeys(
      (previousState) => ({
        ...previousState,

        [visibilityId]:
          !previousState[
          visibilityId
          ],
      }),
    );
  };

  const copyLicenseKey = async (
    licenseKey,
  ) => {
    if (!licenseKey) {
      return;
    }

    try {
      await navigator.clipboard.writeText(
        licenseKey,
      );

      window.alert(
        t(
          'member.licenseKeyCopied',
        ),
      );
    } catch {
      window.alert(
        t(
          'member.licenseKeyCopyFailed',
        ),
      );
    }
  };

  const getStatusLabel = (
    status,
  ) =>
    t(
      `member.licenseStatus.${status}`,
    );

  const getRemainingDaysLabel = (
    remainingDays,
  ) => {
    if (
      remainingDays === null ||
      remainingDays === undefined ||
      !Number.isFinite(
        Number(remainingDays),
      )
    ) {
      return t(
        'memberInfo.products.notApplicable',
      );
    }

    if (
      Number(remainingDays) <= 0
    ) {
      return t('member.expired');
    }

    return t(
      'member.remainingDaysValue',
      {
        days:
          Number(remainingDays),
      },
    );
  };

  const renderLicenseKey = ({
    visibilityId,
    licenseKey,
  }) => {
    const isVisible =
      Boolean(
        visibleLicenseKeys[
        visibilityId
        ],
      );

    return (
      <div className="license-key-row">
        <code>
          {isVisible
            ? licenseKey
            : maskLicenseKey(
              licenseKey,
            )}
        </code>

        <div className="license-key-actions">
          <button
            type="button"
            onClick={() =>
              toggleLicenseKey(
                visibilityId,
              )
            }
          >
            {isVisible
              ? t(
                'member.hideLicenseKey',
              )
              : t(
                'member.showLicenseKey',
              )}
          </button>

          <button
            type="button"
            onClick={() =>
              copyLicenseKey(
                licenseKey,
              )
            }
          >
            {t(
              'member.copyLicenseKey',
            )}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="member-page">
      <section className="license-information-section">
        <header className="license-section-heading">
          <h2>
            {t(
              'member.overview.title',
            )}
          </h2>

          <p>
            {t(
              'member.overview.description',
            )}
          </p>

          {licenseState.status === 'error' && (
            <p className="license-load-error-banner">
              {t('state.dataError.description')}
            </p>
          )}
        </header>

        <div className="registered-machine-summary">
          <div className="registered-machine-summary-heading">
            <span>
              {t(
                'member.overview.registeredMachines',
              )}
            </span>

            <p>
              {t(
                'member.overview.registeredMachinesDescription',
              )}
            </p>
          </div>

          <div className="registered-machine-summary-list">
            {machineProducts.map(
              (product) => {
                const registeredCount =
                  Number(
                    product
                      .machineInformation
                      ?.registeredCount ||
                    0,
                  );

                return (
                  <div
                    key={product.id}
                    className="registered-machine-summary-item"
                  >
                    <span className="registered-machine-summary-dot" />

                    <strong>
                      {product.name}
                    </strong>

                    <b>
                      {product.code ===
                      'HI_LINK'
                        ? t(
                          'memberInfo.products.notApplicable',
                        )
                        : t(
                          'member.registeredMachineCount',
                          {
                            count:
                              registeredCount,
                          },
                        )}
                    </b>
                  </div>
                );
              },
            )}
          </div>
        </div>

        <div className="license-overview-grid">
          <article className="license-overview-card is-license">
            <span>
              {t(
                'member.overview.totalLicenses',
              )}
            </span>

            <strong>
              {t(
                'member.issuedLicenseCount',
                {
                  count:
                    overallStatus
                      .totalLicenseCount,
                },
              )}
            </strong>

            <p>
              {t(
                'member.overview.totalLicensesDescription',
              )}
            </p>
          </article>

          <article className="license-overview-card is-warning">
            <span>
              {t(
                'member.overview.warningLicenses',
              )}
            </span>

            <strong>
              {t(
                'member.issuedLicenseCount',
                {
                  count:
                    overallStatus
                      .warningLicenseCount,
                },
              )}
            </strong>

            <p>
              {t(
                'member.overview.warningLicensesDescription',
              )}
            </p>
          </article>

          <article className="license-overview-card is-critical">
            <span>
              {t(
                'member.overview.criticalLicenses',
              )}
            </span>

            <strong>
              {t(
                'member.issuedLicenseCount',
                {
                  count:
                    overallStatus
                      .criticalLicenseCount,
                },
              )}
            </strong>

            <p>
              {t(
                'member.overview.criticalLicensesDescription',
              )}
            </p>
          </article>
        </div>
      </section>

      <section className="license-information-section">
        <header className="license-section-heading">
          <h2>
            {t(
              'member.productLicense',
            )}
          </h2>
            <div className="license-color-guide">
          <div className="is-normal">
            <span />

            <strong>
              {t(
                'member.overview.colorGuide.normal',
              )}
            </strong>
          </div>

          <div className="is-warning">
            <span />

            <strong>
              {t(
                'member.overview.colorGuide.warning',
              )}
            </strong>
          </div>

          <div className="is-critical">
            <span />

            <strong>
              {t(
                'member.overview.colorGuide.critical',
              )}
            </strong>
          </div>
        </div>
        </header>

        <div className="license-product-list">
          {licenseProducts.map(
            (product) => {
              const license =
                product.licenseInformation;

              if (
                license?.mode ===
                LICENSE_MODES.TERM
              ) {
                const visibilityId =
                  `${product.id}:term`;

                const shouldShowLicenseKey =
                  Boolean(license.key);

                const cardClassName = [
                  'license-product-card',
                  `license-border-${product.borderLevel}`,
                ].join(' ');

                return (
                  <article
                    key={product.id}
                    className={
                      cardClassName
                    }
                  >
                    <header className="license-product-card-heading">
                      <strong>
                        {product.name}
                      </strong>

                      {product.status && (
                        <span
                          className={[
                            'license-status',
                            `is-${product.status}`,
                          ].join(' ')}
                        >
                          {getStatusLabel(
                            product.status,
                          )}
                        </span>
                      )}
                    </header>

                    {license.hasLicense ? (
                      <dl className="license-detail-list">
                        {shouldShowLicenseKey && (
                          <div>
                            <dt>
                              {t(
                                'member.licenseKey',
                              )}
                            </dt>

                            <dd>
                              {renderLicenseKey({
                                visibilityId,

                                licenseKey:
                                  license.key,
                              })}
                            </dd>
                          </div>
                        )}

                        <div>
                          <dt>
                            {t(
                              'member.licenseEndDate',
                            )}
                          </dt>

                          <dd>
                            {formatLicenseDate(
                              license.expiresAt,
                              language,
                            )}
                          </dd>
                        </div>

                        <div>
                          <dt>
                            {t(
                              'member.remainingLicense',
                            )}
                          </dt>

                          <dd>
                            {getRemainingDaysLabel(
                              product.remainingDays,
                            )}
                          </dd>
                        </div>
                      </dl>
                    ) : (
                      <p className="license-empty-message">
                        {t(
                          'member.noLicense',
                        )}
                      </p>
                    )}
                  </article>
                );
              }

              if (
                license?.mode ===
                LICENSE_MODES.PER_MACHINE
              ) {
                const licenseItems =
                  Array.isArray(
                    license.items,
                  )
                    ? license.items
                    : [];

                return (
                  <article
                    key={product.id}
                    className="license-product-card is-per-machine"
                  >
                    <header className="license-product-card-heading">
                      <div>
                        <strong>
                          {product.name}
                        </strong>

                        <small>
                          {t(
                            'member.issuedLicenseCount',
                            {
                              count:
                                licenseItems.length,
                            },
                          )}
                        </small>
                      </div>

                      {product.status && (
                        <span
                          className={[
                            'license-status',
                            `is-${product.status}`,
                          ].join(' ')}
                        >
                          {getStatusLabel(
                            product.status,
                          )}
                        </span>
                      )}
                    </header>

                    {licenseItems.length >
                      0 ? (
                      <div className="machine-license-list">
                        {licenseItems.map(
                          (
                            licenseItem,
                            index,
                          ) => {
                            const visibilityId =
                              `${product.id}:${licenseItem.id}`;

                            const cardClassName =
                              [
                                'machine-license-item',
                                `license-border-${licenseItem.borderLevel}`,
                              ].join(' ');

                            return (
                              <article
                                key={
                                  licenseItem.id
                                }
                                className={
                                  cardClassName
                                }
                              >
                                <header>
                                  <div>
                                    <span>
                                      {index +
                                        1}
                                    </span>

                                    <strong>
                                      {licenseItem.machineName ||
                                        t(
                                          'member.unassigned',
                                        )}
                                    </strong>
                                  </div>

                                  <span
                                    className={[
                                      'license-status',
                                      `is-${licenseItem.status}`,
                                    ].join(' ')}
                                  >
                                    {getStatusLabel(
                                      licenseItem.status,
                                    )}
                                  </span>
                                </header>

                                <dl className="license-detail-list">
                                  <div>
                                    <dt>
                                      {t(
                                        'member.licenseKey',
                                      )}
                                    </dt>

                                    <dd>
                                      {renderLicenseKey({
                                        visibilityId,

                                        licenseKey:
                                          licenseItem.key,
                                      })}
                                    </dd>
                                  </div>

                                  <div>
                                    <dt>
                                      {t(
                                        'member.licenseEndDate',
                                      )}
                                    </dt>

                                    <dd>
                                      {formatLicenseDate(
                                        licenseItem.expiresAt,
                                        language,
                                      )}
                                    </dd>
                                  </div>

                                  <div>
                                    <dt>
                                      {t(
                                        'member.remainingLicense',
                                      )}
                                    </dt>

                                    <dd>
                                      {getRemainingDaysLabel(
                                        licenseItem.remainingDays,
                                      )}
                                    </dd>
                                  </div>
                                </dl>
                              </article>
                            );
                          },
                        )}
                      </div>
                    ) : (
                      <p className="license-empty-message">
                        {t(
                          'member.noLicense',
                        )}
                      </p>
                    )}
                  </article>
                );
              }

              return null;
            },
          )}
        </div>

        <div className="license-rule-notice">
          {t(
            'member.licenseRule',
          )}
        </div>
      </section>
    </div>
  );
}